import { FocusEventHandler } from 'react';
import deepEqual from 'deep-equal';
import { Select as MuiSelect, MenuItem, FormControl, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Colors, ErrorMessage, FormLabel } from '../helpers';
import { useContextField } from '../../../api';
import { useSharedStyles } from '../../../common/sharedStyles';

export interface SelectOption {
    label: string;
    value: any;
    tooltip?: string;
}

const blank = 'BLANK';

export default <T extends string | SelectOption>(props: {
    className?: string;
    name: string;
    label?: string;
    tooltip?: string;
    options: Array<T>;
    placeholder?: string;
    hideValidation?: boolean;
    disabled?: boolean;
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onChange?: (value: any) => void;
}) => {
    const { className, name, label, tooltip, options, placeholder, hideValidation, disabled, onBlur, onChange } = props;
    const [field, _, helper] = useContextField(name);
    const sharedClasses = useSharedStyles();
    const placeholderSelected = field.value === undefined;
    const classes = useStyles();

    const _options: SelectOption[] = options.map(option => {
        const value = typeof option === 'string' ? option : (option as SelectOption).value;
        const label = typeof option === 'string' ? option : (option as SelectOption).label;
        return { label, value: value === '' ? blank : value };
    });

    const option = _options.find(o => deepEqual(field.value, o.value));
    const fieldValue = (field.value === '' || !option) ? blank : (option.value || field.value);

    return <>
        <FormLabel label={label} tooltip={tooltip} />
        <FormControl fullWidth className={className}>
            <MuiSelect
                fullWidth
                variant="outlined"
                name={name}
                disabled={disabled}
                className={sharedClasses.input}
                classes={{ select: classes.select }}
                IconComponent={props => <ExpandMoreIcon {...props} style={{ color: Colors.Black, marginRight: 5 }} />}
                value={placeholderSelected ? 0 : fieldValue}
                onBlur={onBlur}
                onChange={e => {
                    helper.setValue(e.target.value === blank ? '' : e.target.value);
                    onChange && onChange(e.target.value);
                }}>
                {placeholder && <MenuItem value={0} disabled>{placeholder}</MenuItem>}
                {_options.map(({ value, label }) => <MenuItem value={value} key={label}>{label}</MenuItem>)}
            </MuiSelect>
        </FormControl>
        {!hideValidation && <ErrorMessage name={field?.name} />}
    </>
}

//Background color browser fix
const useStyles = makeStyles({
    select: {
        '&:focus': {
            backgroundColor: Colors.White,
            borderRadius: '0.75rem'
        }
    }
}, { name: 'Select' });
